import { useState, useEffect, KeyboardEvent, useCallback, useRef } from 'react';
import { AddressSuggestion } from '../components/AuditForm/types';

interface UseAddressSuggestionsProps {
  inputValue: string;
  onSelect: (address: string) => void;
  onInputChange?: (value: string) => void;
}

interface AddressFormatted {
  display: string;
  original: AddressSuggestion;
  highlights: Array<{ start: number; end: number }>;
  isExactMatch: boolean;
}

const MIN_SEARCH_LENGTH = 3;
const MAX_RESULTS = 5;

export function useAddressSuggestions({
  inputValue,
  onSelect,
  onInputChange,
}: UseAddressSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<AddressFormatted[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const initialValueRef = useRef<string>(inputValue);

  // Memoized function to format address
  const formatAddress = useCallback((suggestion: AddressSuggestion): string => {
    const addr = suggestion.address;
    
    // Get street information
    const street = addr.road || addr.street || addr.pedestrian || '';
    const houseNumber = addr.house_number || '';
    
    // Get location information
    const postcode = addr.postcode || '';
    const city = addr.city || addr.town || addr.village || addr.suburb || addr.municipality || '';

    // Only proceed if we have the minimum required information
    if (!street || !postcode || !city) return '';

    // Format street part
    const streetPart = [street, houseNumber].filter(Boolean).join(' ');
    
    // Format city part
    const cityPart = [postcode, city].filter(Boolean).join(' ');

    // Return formatted address only if we have both parts
    return streetPart && cityPart ? `${streetPart}, ${cityPart}` : '';
  }, []);

  // Memoized function to find highlights in text
  const findHighlights = useCallback((text: string, searchInput: string): Array<{ start: number; end: number }> => {
    const highlights: Array<{ start: number; end: number }> = [];
    const searchTerms = searchInput.toLowerCase().split(/\s+/).filter(Boolean);
    
    searchTerms.forEach(term => {
      let pos = 0;
      const lowerText = text.toLowerCase();
      
      while ((pos = lowerText.indexOf(term, pos)) !== -1) {
        highlights.push({ start: pos, end: pos + term.length });
        pos += term.length;
      }
    });

    // Merge overlapping highlights
    return highlights
      .sort((a, b) => a.start - b.start)
      .reduce((acc, curr) => {
        if (acc.length === 0) return [curr];
        const prev = acc[acc.length - 1];
        if (curr.start <= prev.end) {
          prev.end = Math.max(prev.end, curr.end);
          return acc;
        }
        return [...acc, curr];
      }, [] as Array<{ start: number; end: number }>);
  }, []);

  // Memoized function to filter and deduplicate suggestions
  const processAddresses = useCallback((
    addresses: AddressSuggestion[],
    searchInput: string
  ): AddressFormatted[] => {
    const uniqueAddresses = new Map<string, AddressFormatted>();
    const normalizedInput = searchInput.toLowerCase().trim();
    
    // First pass: collect all addresses and mark exact matches
    addresses.forEach(suggestion => {
      const display = formatAddress(suggestion);
      if (!display) return;

      const normalizedDisplay = display.toLowerCase();
      const isExactMatch = normalizedDisplay === normalizedInput;
      const highlights = findHighlights(display, searchInput);

      // Only process Swiss addresses with valid postcodes
      const addr = suggestion.address;
      const isSwiss = (addr.country_code?.toLowerCase() === 'ch') || 
                     (addr.country?.toLowerCase() === 'switzerland') ||
                     (addr.country?.toLowerCase() === 'schweiz');
      const hasValidPostcode = addr.postcode?.match(/^\d{4}$/);

      if (!isSwiss || !hasValidPostcode) return;

      const existing = uniqueAddresses.get(normalizedDisplay);
      if (!existing || (isExactMatch && !existing.isExactMatch)) {
        uniqueAddresses.set(normalizedDisplay, {
          display,
          original: suggestion,
          highlights,
          isExactMatch
        });
      }
    });

    // Convert to array and sort
    return Array.from(uniqueAddresses.values())
      .sort((a, b) => {
        // First priority: exact matches
        if (a.isExactMatch !== b.isExactMatch) {
          return a.isExactMatch ? -1 : 1;
        }
        // Second priority: number of highlights (more matches = higher priority)
        return b.highlights.length - a.highlights.length;
      })
      .slice(0, MAX_RESULTS);
  }, [formatAddress, findHighlights]);

  // Effect to fetch and process address suggestions
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let isActive = true; // Flag to prevent updates if component unmounts

    const fetchSuggestions = async () => {
      // Don't show suggestions if user hasn't started typing yet
      if (!isUserTyping) {
        return;
      }

      if (inputValue.length < MIN_SEARCH_LENGTH) {
        setSuggestions([]);
        setSelectedIndex(-1);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `format=json&` +
          `q=${encodeURIComponent(inputValue)}&` +
          `countrycodes=ch&` +
          `limit=10&` +
          `addressdetails=1&` +
          `featuretype=street`
        );

        if (!isActive) return; // Don't update state if component unmounted

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: AddressSuggestion[] = await response.json();
        const processed = processAddresses(data, inputValue);
        setSuggestions(processed);
        setSelectedIndex(-1);
      } catch (err) {
        if (!isActive) return;
        console.error('Error fetching address suggestions:', err);
        setError('Adressvorschläge konnten nicht geladen werden');
        setSuggestions([]);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    timeoutId = setTimeout(fetchSuggestions, 300);

    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [inputValue, processAddresses, isUserTyping]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setSelectedIndex(-1);
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Mark that user is actively typing
    setIsUserTyping(true);
    onInputChange?.(event.currentTarget.value);
    if (suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          onSelect(suggestions[selectedIndex].display);
          clearSuggestions();
        }
        break;
      case 'Tab':
        event.preventDefault();
        // If there's an exact match, use that
        const exactMatch = suggestions.find(s => s.isExactMatch);
        if (exactMatch) {
          onSelect(exactMatch.display);
        } else if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          onSelect(suggestions[selectedIndex].display);
        } else if (suggestions.length === 1) {
          onSelect(suggestions[0].display);
        }
        clearSuggestions();
        break;
      case 'Escape':
        clearSuggestions();
        break;
    }
  }, [suggestions, selectedIndex, onSelect, clearSuggestions, onInputChange]);

  const handleSuggestionClick = useCallback((suggestion: AddressFormatted) => {
    onSelect(suggestion.display);
    clearSuggestions();
  }, [onSelect, clearSuggestions]);

  const handleBlur = useCallback(() => {
    // Delay clearing to allow click events on suggestions to fire first
    setTimeout(() => {
      clearSuggestions();
    }, 200);
  }, [clearSuggestions]);

  const handleFocus = useCallback(() => {
    // Only show suggestions if the value has changed from initial
    if (inputValue !== initialValueRef.current) {
      setIsUserTyping(true);
    }
  }, [inputValue]);

  const handleChange = useCallback(() => {
    // Mark that user is typing when they change the input
    setIsUserTyping(true);
  }, []);

  return {
    suggestions,
    isLoading,
    selectedIndex,
    error,
    handleKeyDown,
    handleSuggestionClick,
    handleBlur,
    handleFocus,
    handleChange,
    clearSuggestions
  };
}
